import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  Divider, 
  CircularProgress,
  useTheme,
  Paper,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import { 
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicDisplay } from '../../components/PublicDisplayWrapper';
import { format } from 'date-fns';
import config from '../../config';

function CycleCountDisplay({ isPublic = false, hardcodedToken = null }) {
  const { token } = useAuth();
  // Always call the hook, but only use its value if isPublic is true and no hardcoded token
  const publicDisplayContext = usePublicDisplay();
  // Use hardcoded token if provided, otherwise use the context token
  const publicToken = isPublic ? (hardcodedToken || publicDisplayContext.publicToken) : null;
  const theme = useTheme();
  
  // State for cycle count data
  const [cycleCounts, setCycleCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Current time for the clock
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update the time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);

  // Fetch cycle count data
  const fetchCycleCounts = useCallback(async () => {
    try {
      setLoading(true);
      
      // Use different endpoints based on whether this is public or authenticated
      let endpoint, headers;
      
      if (isPublic && publicToken) {
        endpoint = `${config.apiUrl}/public/cyclecount?token=${publicToken}`;
        headers = { 'Content-Type': 'application/json' };
        console.log('Using public endpoint:', endpoint);
      } else {
        endpoint = `${config.apiUrl}/cyclecount`;
        headers = { 'Authorization': `Bearer ${token}` };
        console.log('Using authenticated endpoint:', endpoint);
      }
      
      const response = await fetch(endpoint, { headers });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error:', response.status, errorData);
        throw new Error(`API error: ${response.status} ${errorData.error || 'Unknown error'}`);
      }
      
      const data = await response.json();
      console.log('Raw cycle count data from API:', data);
      
      // Check if data is an array before processing
      if (!Array.isArray(data)) {
        console.error('Expected array for cycle counts, got:', typeof data, data);
        throw new Error('Invalid data format received from API');
      }
      
      // Sort by status (open first) and then by reportedAt date
      const sortedCycleCounts = data.sort((a, b) => {
        if (a.status === b.status) {
          return new Date(b.createdAt || b.reportedAt) - new Date(a.createdAt || a.reportedAt);
        }
        return a.status === 'open' ? -1 : 1;
      });
      
      setCycleCounts(sortedCycleCounts);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching cycle count data:', error);
      setError(error.message || 'Failed to load cycle count data');
      setCycleCounts([]); // Set empty array to prevent processing errors
      setLoading(false);
    }
  }, [token, isPublic, publicToken]);

  useEffect(() => {
    // Fetch data
    fetchCycleCounts();
    
    // Set up refresh interval (every 5 minutes)
    const refreshInterval = setInterval(() => {
      fetchCycleCounts();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [fetchCycleCounts]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  };

  // Helper function to get status chip
  const getStatusChip = (status) => {
    switch (status) {
      case 'open':
        return <Chip label="OPEN" color="warning" sx={{ fontWeight: 'bold' }} />;
      case 'completed':
        return <Chip label="COMPLETED" color="success" sx={{ fontWeight: 'bold' }} />;
      default:
        return <Chip label={status.toUpperCase()} sx={{ fontWeight: 'bold' }} />;
    }
  };

  // Calculate statistics
  const calculateStats = () => {
    const pending = cycleCounts.filter(count => count.status === 'open').length;
    const completed = cycleCounts.filter(count => count.status === 'completed').length;
    
    return { pending, completed };
  };
  
  const stats = calculateStats();
  
  return (
    <Box sx={{ 
      flexGrow: 1, 
      p: 2,
      height: '100vh', 
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header with time and date - styled like TimeOffDisplay but with blue background */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 2, 
          mb: 2,
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: theme.palette.primary.main,
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InventoryIcon sx={{ fontSize: 24, mr: 1 }} />
          <Typography variant="h4" fontWeight="normal">
            Cycle Count Requests
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="subtitle1">
            {format(currentTime, 'EEEE, MMMM d, yyyy')}
          </Typography>
        </Box>
      </Paper>
      
      {/* Main content */}
      <Card 
        elevation={3}
        sx={{ 
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          borderTop: `6px solid ${theme.palette.secondary.main}`,
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          p: 1,
          '&:last-child': { 
            pb: 1
          }
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress size={60} />
            </Box>
          ) : error ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="error" variant="h5">{error}</Typography>
            </Box>
          ) : (
            <Box>
              {/* Statistics Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: theme.palette.warning.main,
                      color: 'white',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <WarningIcon sx={{ mr: 1, fontSize: 28 }} />
                      <Typography variant="h6">Pending Counts</Typography>
                    </Box>
                    <Typography variant="h3" fontWeight="bold">{stats.pending}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: theme.palette.success.main,
                      color: 'white',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CheckCircleIcon sx={{ mr: 1, fontSize: 28 }} />
                      <Typography variant="h6">Completed Counts</Typography>
                    </Box>
                    <Typography variant="h3" fontWeight="bold">{stats.completed}</Typography>
                  </Paper>
                </Grid>
              </Grid>
              
              {/* Cycle Count Table */}
              <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 3 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: theme.palette.primary.main }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Part Number</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Location</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reported Qty</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actual Qty</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reported By</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Counted By</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Created At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cycleCounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No cycle counts found
                        </TableCell>
                      </TableRow>
                    ) : (
                      cycleCounts.map((count) => (
                        <TableRow 
                          key={count._id}
                          sx={{ 
                            '&:nth-of-type(odd)': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.08)' }
                          }}
                        >
                          <TableCell>{count.partNumber}</TableCell>
                          <TableCell>{count.location}</TableCell>
                          <TableCell>{count.reportedQty}</TableCell>
                          <TableCell>
                            {count.status === 'completed' ? (
                              <Box>
                                <Typography variant="body2">
                                  {count.actualQty}
                                  {count.actualQty !== undefined && count.reportedQty !== undefined && (
                                    <Typography 
                                      component="span" 
                                      variant="caption" 
                                      sx={{ 
                                        ml: 1,
                                        fontWeight: 'bold',
                                        color: count.actualQty > count.reportedQty 
                                          ? 'success.main' 
                                          : count.actualQty < count.reportedQty 
                                            ? 'error.main' 
                                            : 'text.secondary'
                                      }}
                                    >
                                      ({count.actualQty - count.reportedQty > 0 ? '+' : ''}
                                      {count.actualQty - count.reportedQty})
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{getStatusChip(count.status)}</TableCell>
                          <TableCell>
                            {(count.reportedByUser || count.reportedBy) ? 
                              `${(count.reportedByUser || count.reportedBy).firstName} ${(count.reportedByUser || count.reportedBy).lastName}` : 
                              'Unknown'}
                          </TableCell>
                          <TableCell>
                            {(count.countedByUser || count.countedBy) ? 
                              `${(count.countedByUser || count.countedBy).firstName} ${(count.countedByUser || count.countedBy).lastName}` : 
                              '-'}
                          </TableCell>
                          <TableCell>{formatDate(count.createdAt)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default CycleCountDisplay; 